import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Button, Card, Chip, IconButton, Stack, Typography, useTheme } from '@mui/material';
import Fade from '@mui/material/Fade';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

const ADS = [
  {
    id: 'trade-nation-starter',
    title: 'Start trading with TradeNation and FlockTrade',
    subtitle: 'Grow with a regulated broker that offers fixed spreads and direct support.',
    image: 'https://tradenation.ck-cdn.com/tn/serve/?cid=404036',
    link: 'https://go.tradenation.com/visit/?bta=36156',
    ctaLabel: 'Get started',
    partner: 'TradeNation Partner Ad',
    bulletPoints: [
      'Fixed low spreads with no surprise fees',
      'Integrated access via TradingView and MT4',
      'Exclusive mentoring from the FlockTrade team',
    ],
    background: 'linear-gradient(135deg, rgba(10,19,45,0.95), rgba(14,118,196,0.82))',
    accent: '#38bdf8',
    disclaimer: 'Sponsored content. Trading CFDs involves risk of loss.',
  },
  {
    id: 'tradingview-direct',
    title: 'Trade directly inside TradingView',
    subtitle: 'Place orders without leaving your chart and monitor positions in real time.',
    image: 'https://tradenation.ck-cdn.com/tn/serve/?cid=404043',
    link: 'https://go.tradenation.com/visit/?bta=36156',
    ctaLabel: 'Connect account',
    partner: 'TradingView x TradeNation',
    bulletPoints: [
      'One-click execution on every device',
      'Stay synced between web and mobile platforms',
      'Backed by TradeNation fixed spreads and support',
    ],
    background: 'linear-gradient(135deg, rgba(17,24,54,0.95), rgba(127,17,224,0.75))',
    accent: '#a855f7',
    disclaimer: 'Sponsored content. Trading CFDs involves risk of loss.',
  },
];

const AdCard = ({ rotationInterval = 10000 }) => {
  const theme = useTheme();
  const [currentAdIndex, setCurrentAdIndex] = useState(0);
  const rotationRef = useRef(null);
  const hasMultipleAds = ADS.length > 1;

  const moveToNext = useCallback(() => {
    setCurrentAdIndex((prevIndex) => (prevIndex + 1) % ADS.length);
  }, []);

  const startRotation = useCallback(() => {
    if (!hasMultipleAds) {
      return;
    }

    clearInterval(rotationRef.current);
    rotationRef.current = setInterval(() => {
      moveToNext();
    }, rotationInterval);
  }, [hasMultipleAds, moveToNext, rotationInterval]);

  useEffect(() => {
    startRotation();
    return () => clearInterval(rotationRef.current);
  }, [startRotation]);

  const handleNavigate = useCallback(
    (direction) => {
      if (!hasMultipleAds) {
        return;
      }

      startRotation();
      setCurrentAdIndex((prevIndex) => {
        if (direction === 'next') {
          return (prevIndex + 1) % ADS.length;
        }

        return (prevIndex - 1 + ADS.length) % ADS.length;
      });
    },
    [hasMultipleAds, startRotation],
  );

  const handleSelect = useCallback(
    (index) => {
      if (!hasMultipleAds || index === currentAdIndex) {
        return;
      }

      startRotation();
      setCurrentAdIndex(index);
    },
    [currentAdIndex, hasMultipleAds, startRotation],
  );

  const currentAd = ADS[currentAdIndex];

  return (
    <Card
      elevation={0}
      sx={{
        mt: { xs: 6, md: 8 },
        mx: 'auto',
        maxWidth: 1100,
        background: 'transparent',
        boxShadow: 'none',
      }}
    >
      <Fade in key={currentAd.id} timeout={400}>
        <Box
          sx={{
            position: 'relative',
            overflow: 'hidden',
            borderRadius: { xs: 4, md: 5 },
            background: currentAd.background,
            color: theme.palette.common.white,
            px: { xs: 3, md: 5 },
            py: { xs: 4, md: 6 },
            boxShadow: '0 24px 60px rgba(4,12,28,0.35)',
          }}
        >
          <Chip
            label={currentAd.partner}
            size="small"
            sx={{
              position: 'absolute',
              top: { xs: 20, md: 24 },
              left: { xs: 20, md: 28 },
              backgroundColor: 'rgba(255,255,255,0.18)',
              color: theme.palette.common.white,
              fontWeight: 600,
              letterSpacing: 0.6,
              textTransform: 'uppercase',
            }}
          />

          <Box
            sx={{
              display: 'grid',
              gap: { xs: 3, md: 6 },
              gridTemplateColumns: {
                xs: '1fr',
                md: '1.15fr 0.85fr',
              },
              alignItems: 'center',
            }}
          >
            <Stack spacing={3}>
              <Stack
                spacing={1}
                sx={{
                  maxWidth: { xs: '100%', md: '85%' },
                }}
              >
                <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1.25 }}>
                  {currentAd.title}
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.85 }}>
                  {currentAd.subtitle}
                </Typography>
              </Stack>

              {currentAd.bulletPoints?.length > 0 && (
                <Stack spacing={1.5}>
                  {currentAd.bulletPoints.map((bullet) => (
                    <Stack key={bullet} direction="row" spacing={1.5} alignItems="center">
                      <CheckCircleOutlineIcon sx={{ color: currentAd.accent, fontSize: 20 }} />
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        {bullet}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              )}

              <Stack spacing={1.5} alignItems={{ xs: 'stretch', sm: 'flex-start' }}>
                <Button
                  size="large"
                  href={currentAd.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="contained"
                  endIcon={<OpenInNewIcon />}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    alignSelf: { xs: 'stretch', sm: 'flex-start' },
                    borderRadius: 3,
                    px: { xs: 3, sm: 4 },
                    py: 1.4,
                    boxShadow: '0 15px 35px rgba(0,0,0,0.35)',
                  }}
                >
                  {currentAd.ctaLabel}
                </Button>

                {currentAd.disclaimer && (
                  <Typography variant="caption" sx={{ opacity: 0.75 }}>
                    {currentAd.disclaimer}
                  </Typography>
                )}
              </Stack>
            </Stack>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                borderRadius: { xs: 3, md: 4 },
                backgroundColor: 'rgba(255,255,255,0.06)',
                p: { xs: 3, md: 4 },
                minHeight: { xs: 220, md: 260 },
              }}
            >
              <Box
                component="img"
                src={currentAd.image}
                alt={currentAd.title}
                sx={{
                  width: '100%',
                  maxWidth: { xs: 320, md: 360 },
                  objectFit: 'contain',
                  filter: 'drop-shadow(0 18px 35px rgba(0,0,0,0.45))',
                }}
              />
            </Box>
          </Box>

          {hasMultipleAds && (
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              sx={{
                position: 'absolute',
                bottom: { xs: 18, md: 22 },
                right: { xs: 20, md: 28 },
              }}
            >
              <IconButton
                aria-label="Show previous ad"
                onClick={() => handleNavigate('prev')}
                sx={{
                  backgroundColor: 'rgba(255,255,255,0.16)',
                  color: theme.palette.common.white,
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.24)',
                  },
                }}
                size="small"
              >
                <ArrowBackIosNewIcon fontSize="small" />
              </IconButton>

              <Stack direction="row" spacing={1}>
                {ADS.map((ad, index) => (
                  <Box
                    key={ad.id}
                    role="button"
                    tabIndex={0}
                    aria-label={`Show ad ${index + 1}`}
                    onClick={() => handleSelect(index)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        handleSelect(index);
                      }
                    }}
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      cursor: 'pointer',
                      outline: 'none',
                      backgroundColor:
                        index === currentAdIndex
                          ? theme.palette.common.white
                          : 'rgba(255,255,255,0.35)',
                    }}
                  />
                ))}
              </Stack>

              <IconButton
                aria-label="Show next ad"
                onClick={() => handleNavigate('next')}
                sx={{
                  backgroundColor: 'rgba(255,255,255,0.16)',
                  color: theme.palette.common.white,
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.24)',
                  },
                }}
                size="small"
              >
                <ArrowForwardIosIcon fontSize="small" />
              </IconButton>
            </Stack>
          )}
        </Box>
      </Fade>
    </Card>
  );
};

export default AdCard;
